import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { NavLink, useLocation } from "react-router";

import { ROUTES } from "~/constants/routing";
import { INSIGHTS_SIDEBAR_VISIBLE_CATEGORIES } from "~/feature/insights/constants";
import type { IInsightsSidebarData } from "~/feature/insights/types";

import * as Styled from "./InsightsSidebar.styles";

interface InsightsSidebarProps {
  data: IInsightsSidebarData;
  /** Mobile accordion (collapsed by default). Desktop uses sticky panel. */
  variant?: "desktop" | "mobile";
}

const getActiveCategorySlug = (pathname: string): string | null => {
  const match = pathname.match(/^\/insights\/category\/(.+)$/);
  return match ? match[1] : null;
};

const isAllCategoriesActive = (pathname: string): boolean =>
  pathname === ROUTES.insights.path ||
  pathname === ROUTES.insightsAll.path ||
  pathname === `${ROUTES.insights.path}/`;

interface SidebarPanelProps {
  data: IInsightsSidebarData;
  bordered?: boolean;
  onFilterNavigate?: () => void;
}

const SidebarPanel: FC<SidebarPanelProps> = ({
  data,
  bordered = true,
  onFilterNavigate,
}) => {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const activeCategorySlug = getActiveCategorySlug(pathname);
  const allActive = !activeCategorySlug && isAllCategoriesActive(pathname);

  const visibleCategories = data.categories.slice(
    0,
    INSIGHTS_SIDEBAR_VISIBLE_CATEGORIES
  );
  const overflowCategories = data.categories.slice(
    INSIGHTS_SIDEBAR_VISIBLE_CATEGORIES
  );

  const renderCategoryRow = (slug: string, name: string, count: number) => {
    const isActive = activeCategorySlug === slug;
    return (
      <Styled.RowButton
        key={slug}
        component={NavLink}
        to={ROUTES.insightsCategory.createURL(slug)}
        $isActive={isActive}
        onClick={onFilterNavigate}
      >
        <Styled.RowLabel>{name}</Styled.RowLabel>
        <Styled.RowCount $isActive={isActive}>{count}</Styled.RowCount>
      </Styled.RowButton>
    );
  };

  const content = (
    <>
      <Styled.Section>
        <Styled.SectionLabel>Categories</Styled.SectionLabel>
        <Styled.RowButton
          component={NavLink}
          to={ROUTES.insights.createURL()}
          $isActive={allActive}
          onClick={onFilterNavigate}
        >
          <Styled.RowLabel>All</Styled.RowLabel>
          <Styled.RowCount $isActive={allActive}>
            {data.allCount}
          </Styled.RowCount>
        </Styled.RowButton>

        {visibleCategories.map((category) =>
          renderCategoryRow(category.slug, category.name, category.count)
        )}

        {overflowCategories.length > 0 && (
          <>
            <Styled.MoreButton
              $open={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
              endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            >
              More
            </Styled.MoreButton>
            {moreOpen &&
              overflowCategories.map((category) =>
                renderCategoryRow(category.slug, category.name, category.count)
              )}
          </>
        )}
      </Styled.Section>

      {data.types.length > 0 && (
        <>
          <Styled.SectionDivider />
          <Styled.Section>
            <Styled.SectionLabel>Types</Styled.SectionLabel>
            {data.types.map((type) => (
              <Styled.RowButton
                key={type.slug}
                component={NavLink}
                to={ROUTES.insightsType.createURL(type.slug)}
                onClick={onFilterNavigate}
              >
                <Styled.RowLabel>{type.name}</Styled.RowLabel>
                <Styled.RowCount>{type.count}</Styled.RowCount>
              </Styled.RowButton>
            ))}
          </Styled.Section>
        </>
      )}

      {data.tags.length > 0 && (
        <>
          <Styled.SectionDivider />
          <Styled.Section>
            <Styled.SectionLabel>Tags</Styled.SectionLabel>
            <Styled.TagsWrap>
              {data.tags.map((tag) => (
                <Styled.TagPill
                  key={tag.slug}
                  to={ROUTES.insightsTag.createURL(tag.slug)}
                  onClick={onFilterNavigate}
                >
                  {tag.name}
                </Styled.TagPill>
              ))}
            </Styled.TagsWrap>
          </Styled.Section>
        </>
      )}
    </>
  );

  if (!bordered) {
    return <Box sx={{ px: 1, pb: 0.5 }}>{content}</Box>;
  }

  return <Styled.Panel>{content}</Styled.Panel>;
};

export const InsightsSidebar: FC<InsightsSidebarProps> = ({
  data,
  variant = "desktop",
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasContent =
    data.categories.length > 0 || data.types.length > 0 || data.tags.length > 0;

  if (!hasContent) {
    return null;
  }

  if (variant === "mobile") {
    return (
      <Styled.MobileAccordion
        disableGutters
        expanded={filtersOpen}
        onChange={(_, expanded) => setFiltersOpen(expanded)}
      >
        <Styled.MobileAccordionSummary
          expandIcon={<KeyboardArrowDownIcon />}
          aria-controls="insights-filters-content"
          id="insights-filters-header"
        >
          <Styled.FiltersLabel>Filters</Styled.FiltersLabel>
        </Styled.MobileAccordionSummary>
        <Styled.MobileAccordionDetails>
          <SidebarPanel
            data={data}
            bordered={false}
            onFilterNavigate={() => setFiltersOpen(false)}
          />
        </Styled.MobileAccordionDetails>
      </Styled.MobileAccordion>
    );
  }

  return (
    <Styled.StickyDesktop>
      <SidebarPanel data={data} />
    </Styled.StickyDesktop>
  );
};
